import {FormArray, FormControl, FormGroup} from '@angular/forms';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    UsersService,
    UserWithPermissionsInterface,
    PermissionModel,
    ClientUserReportModel,
    IndividualExtendedModel,
    UserWithPermissionsModel,
    AccountPermissionUpdateInterface,
} from '@spa/api';

import {Helper} from 'appShared/classes';
import {CheckboxControl} from 'appShared/classes/controls';

import {ClientAccessRightsTypesEnum, ClientPermissionsFormErrorStatesEnum} from '../enums';

import {
    ClientRightEditPermissionCheckbox,
    ClientReportsRightsFormArraySelector,
    ClientManagerialRightsFormArraySelector,
    ClientCashOperationsRightsFormArraySelector,
    ClientsMdsRightsFormArraySelector,
    ClientsUsersRightsFormArraySelector,
    ClientsCurrencyExchangeRightsFormArraySelector, ClientsFundPortalRightsFormArraySelector,
} from 'appAssets/descriptors';

export class ClientPermissionsForm {
    private readonly user: IndividualExtendedModel | ClientUserReportModel;

    private userWithPermissions: UserWithPermissionsModel;
    private usersService: UsersService;
    private portalPermissions: PermissionModel[];

    public formGroup: FormGroup;

    public managerialRightsControls: CheckboxControl[];
    public cashOperationsRightsControls: CheckboxControl[];
    public reportsRightsControls: CheckboxControl[];
    public mdsRightsControls: CheckboxControl[];
    public usersRightsControls: CheckboxControl[];
    public currencyExchangeRightsControls: CheckboxControl[];
    public fundPortalRightsControls: CheckboxControl[];

    public managerialRightsFormArraySelector: string;
    public cashOperationsRightsFormArraySelector: string;
    public reportsRightsFormArraySelector: string;
    public mdsRightsFormArraySelector: string;
    public usersRightsFormArraySelector: string;
    public currencyExchangeRightsFormArraySelector: string;
    public fundPortalRightsFormArraySelector: string;

    constructor(
        userWithPermissions: UserWithPermissionsModel,
        portalPermissions: PermissionModel[],
        usersService: UsersService,
        user: IndividualExtendedModel | ClientUserReportModel
    ) {
        this.usersService = usersService;
        this.portalPermissions = portalPermissions;
        this.userWithPermissions = userWithPermissions;
        this.user = user;

        this.managerialRightsFormArraySelector = ClientManagerialRightsFormArraySelector;
        this.cashOperationsRightsFormArraySelector = ClientCashOperationsRightsFormArraySelector;
        this.reportsRightsFormArraySelector = ClientReportsRightsFormArraySelector;
        this.mdsRightsFormArraySelector = ClientsMdsRightsFormArraySelector;
        this.usersRightsFormArraySelector = ClientsUsersRightsFormArraySelector;
        this.currencyExchangeRightsFormArraySelector = ClientsCurrencyExchangeRightsFormArraySelector;
        this.fundPortalRightsFormArraySelector = ClientsFundPortalRightsFormArraySelector;
    }

    private initPermissionsFormArray(userPermissions: string[], rightsType: number): FormArray {
        const clientTradingCategory = (this.user instanceof IndividualExtendedModel) ? this.user.tradingCategory :
            this.user.clientTradingCategory;

        const permissionStrings = this.getRightsByType(rightsType, clientTradingCategory);

        const formArray = new FormArray([]);

        const filteredPortalPermissions = this.portalPermissions.filter((permission: PermissionModel) => {
            return (permissionStrings.indexOf(permission.permissionString) >= 0);
        }).sort((current: PermissionModel, next: PermissionModel) => {
            const currentPermissionPosition = Helper.enumsHelper().permissionsEnumHelper()
                .getPortalMainPermissionPosition(rightsType, current.permissionString);

            const nextPermissionPosition = Helper.enumsHelper().permissionsEnumHelper()
                .getPortalMainPermissionPosition(rightsType, next.permissionString);

            return currentPermissionPosition.row - nextPermissionPosition.row;
        });

        if (filteredPortalPermissions.length > 0) {
            filteredPortalPermissions.map((permission: PermissionModel, index: number) => {
                const isChecked = userPermissions.indexOf(permission.permissionString) >= 0;

                const controlName = this.getRightNameByType(rightsType, permission.permissionString);

                const checkboxControl = ClientRightEditPermissionCheckbox(
                    index.toString(),
                    controlName,
                    permission.id,
                    isChecked
                );

                const formControl = new FormControl(checkboxControl.value, checkboxControl.validators);

                this.pushControl(rightsType, checkboxControl);

                formArray.push(formControl);

            });
        }

        return formArray;
    }

    private getRightsByType(type: number, clientTradingCategory: number): string[] {
        switch (type) {
            case ClientAccessRightsTypesEnum.ManagerialRights:
                return Helper.enumsHelper().permissionsEnumHelper()
                    .getPortalManagerialRightsPermissions(clientTradingCategory);
            case ClientAccessRightsTypesEnum.CashOperationsRights:
                return Helper.enumsHelper().permissionsEnumHelper()
                    .getPortalCashOperationsOperationsPermissions();
            case ClientAccessRightsTypesEnum.ReportsRights:
                return Helper.enumsHelper().permissionsEnumHelper()
                    .getPortalReportsPermissions();
            case ClientAccessRightsTypesEnum.MdsRights:
                return Helper.enumsHelper().permissionsEnumHelper()
                    .getPortalMdsPermissions();
            case ClientAccessRightsTypesEnum.UsersRights:
                return Helper.enumsHelper().permissionsEnumHelper()
                    .getPortalUsersPermissions();
            case ClientAccessRightsTypesEnum.CurrencyExchangeRights:
                return Helper.enumsHelper().permissionsEnumHelper()
                    .getPortalCurrencyExchangePermissions();
            case ClientAccessRightsTypesEnum.FundPortalRights:
                return Helper.enumsHelper().permissionsEnumHelper()
                    .getPortalFundPortalPermissions();
        }
    }

    private getRightNameByType(type: number, permissionString: string): string {
        switch (type) {
            case ClientAccessRightsTypesEnum.ManagerialRights:
                return Helper.textsHelper().getPortalManagerialRightsPermissionName(permissionString);
            case ClientAccessRightsTypesEnum.CashOperationsRights:
                return Helper.textsHelper().getPortalCashOperationsPermissionName(permissionString);
            case ClientAccessRightsTypesEnum.ReportsRights:
                return Helper.textsHelper().getPortalReportsPermissionName(permissionString);
            case ClientAccessRightsTypesEnum.MdsRights:
                return Helper.textsHelper().getPortalMdsPermissionName(permissionString);
            case ClientAccessRightsTypesEnum.UsersRights:
                return Helper.textsHelper().getPortalUsersPermissionName(permissionString);
            case ClientAccessRightsTypesEnum.CurrencyExchangeRights:
                return Helper.textsHelper().getPortalCurrencyExchangePermissionName(permissionString);
            case ClientAccessRightsTypesEnum.FundPortalRights:
                return Helper.textsHelper().getPortalFundPortalPermissionName(permissionString);

        }
    }

    private pushControl(rightsType: number, control: CheckboxControl): void {
        switch (rightsType) {
            case ClientAccessRightsTypesEnum.ManagerialRights:
                this.managerialRightsControls.push(control);
                break;
            case ClientAccessRightsTypesEnum.CashOperationsRights:
                this.cashOperationsRightsControls.push(control);
                break;
            case ClientAccessRightsTypesEnum.ReportsRights:
                this.reportsRightsControls.push(control);
                break;
            case ClientAccessRightsTypesEnum.MdsRights:
                this.mdsRightsControls.push(control);
                break;
            case ClientAccessRightsTypesEnum.UsersRights:
                this.usersRightsControls.push(control);
                break;
            case ClientAccessRightsTypesEnum.CurrencyExchangeRights:
                this.currencyExchangeRightsControls.push(control);
                break;
            case ClientAccessRightsTypesEnum.FundPortalRights:
                this.fundPortalRightsControls.push(control);
                break;
        }
    }

    initForm(): void {
        let userPermissions = [];

        this.managerialRightsControls = [];
        this.reportsRightsControls = [];
        this.cashOperationsRightsControls = [];
        this.mdsRightsControls = [];
        this.usersRightsControls = [];
        this.currencyExchangeRightsControls = [];
        this.fundPortalRightsControls = [];

        this.formGroup = new FormGroup({});

        if (this.userWithPermissions && (this.userWithPermissions.permissions.length > 0)) {
            userPermissions = this.userWithPermissions.transform().permissions;
        }

        this.formGroup.addControl(
            this.managerialRightsFormArraySelector,
            this.initPermissionsFormArray(userPermissions, ClientAccessRightsTypesEnum.ManagerialRights)
        );

        this.formGroup.addControl(
            this.cashOperationsRightsFormArraySelector,
            this.initPermissionsFormArray(userPermissions, ClientAccessRightsTypesEnum.CashOperationsRights)
        );

        this.formGroup.addControl(
            this.reportsRightsFormArraySelector,
            this.initPermissionsFormArray(userPermissions, ClientAccessRightsTypesEnum.ReportsRights)
        );

        this.formGroup.addControl(this.mdsRightsFormArraySelector,
            this.initPermissionsFormArray(userPermissions, ClientAccessRightsTypesEnum.MdsRights)
        );

        this.formGroup.addControl(this.usersRightsFormArraySelector,
            this.initPermissionsFormArray(userPermissions, ClientAccessRightsTypesEnum.UsersRights)
        );

        this.formGroup.addControl(this.currencyExchangeRightsFormArraySelector,
            this.initPermissionsFormArray(userPermissions, ClientAccessRightsTypesEnum.CurrencyExchangeRights)
        );

        this.formGroup.addControl(this.fundPortalRightsFormArraySelector,
            this.initPermissionsFormArray(userPermissions, ClientAccessRightsTypesEnum.FundPortalRights)
        );
    }

    toggleDisableFormControlsState(areDisabled: boolean): void {
        this.managerialRightsControls.forEach((control: CheckboxControl) => control.disabled = areDisabled);
        this.cashOperationsRightsControls.forEach((control: CheckboxControl) => control.disabled = areDisabled);
        this.reportsRightsControls.forEach((control: CheckboxControl) => control.disabled = areDisabled);
        this.mdsRightsControls.forEach((control: CheckboxControl) => control.disabled = areDisabled);
        this.usersRightsControls.forEach((control: CheckboxControl) => control.disabled = areDisabled);
        this.currencyExchangeRightsControls.forEach((control: CheckboxControl) => control.disabled = areDisabled);
        this.fundPortalRightsControls.forEach((control: CheckboxControl) => control.disabled = areDisabled);
    }

    createRequest(): Observable<ApiResponse<ApiError[] | AccountPermissionUpdateInterface>> {
        const managerialFormArray = this.formGroup.controls[this.managerialRightsFormArraySelector] as FormArray;
        const cashOperationsFormArray = this.formGroup.controls[this.cashOperationsRightsFormArraySelector] as FormArray;
        const reportsFormArray = this.formGroup.controls[this.reportsRightsFormArraySelector] as FormArray;
        const mdsFormArray = this.formGroup.controls[this.mdsRightsFormArraySelector] as FormArray;
        const usersFormArray = this.formGroup.controls[this.usersRightsFormArraySelector] as FormArray;
        const currencyExchangeFormArray = this.formGroup.controls[this.currencyExchangeRightsFormArraySelector] as FormArray;
        const fundPortalFormArray = this.formGroup.controls[this.fundPortalRightsFormArraySelector] as FormArray;
        const fundPortalPermissions: number[] = [];

        const clonedModel = this.userWithPermissions.serialize();

        const allFundPortalPermissions = this.portalPermissions.filter(permission => {
            return fundPortalFormArray.value.indexOf(permission.id) >= 0 || Helper.enumsHelper().permissionsEnumHelper()
                .getPortalFundPortalPermissions(true).indexOf(permission.permissionString) >= 0;
        });

        if (fundPortalFormArray.value.length > 0 && fundPortalFormArray.value[0]) {
            fundPortalPermissions.push(...allFundPortalPermissions.map(p => p.id));
            clonedModel.permissions.push(...allFundPortalPermissions.map(p => p.serialize()));
        }

        const isLegalClient = (this.user instanceof ClientUserReportModel) && this.user.isLegalRepresentative;

        const filteredFormValues = managerialFormArray.value.concat(
            cashOperationsFormArray.value,
            reportsFormArray.value,
            mdsFormArray.value,
            usersFormArray.value,
            currencyExchangeFormArray.value,
            fundPortalPermissions
        ).filter((id: number) => {
            return (id !== null);
        });

        /**
         * Get selected permission from the panel and default permissions for the portals.
         * Two additional permissions are set by default if the client is legal representative of a company.
         */
        const selectedPermissionsModels = this.portalPermissions.filter((permission: PermissionModel) => {
            return (filteredFormValues.indexOf(permission.id) >= 0) || (Helper.enumsHelper().permissionsEnumHelper()
                .getDefaultPortalMainPermissions(isLegalClient).indexOf(permission.permissionString) >= 0);
        });

        this.userWithPermissions.updateUserPermissions(selectedPermissionsModels);

        return this.usersService.updatePermissions(this.userWithPermissions).pipe(
            map((response: ApiResponse<ApiError[] | UserWithPermissionsInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    this.userWithPermissions = new UserWithPermissionsModel(clonedModel);

                    return Helper.errorsHelper().createErrorResponse(
                        response,
                        ClientPermissionsFormErrorStatesEnum.UpdateUserPermissionsFailed
                    );
                }

                return Helper.createSuccessResponse<AccountPermissionUpdateInterface>
                    ({
                        cashAccountPermissionUpdated: managerialFormArray.controls[0].touched,
                        canCreateNewCashAccount: this.managerialRightsControls[0].getChecked(), successResponse: true
                    });
            })
        );
    }
}
